import { almostZero } from "@xola/ui-kit";
import { sumBy } from "lodash";
import React from "react";
import { LineItem, LineItemCode, LineItemTemplate, LineItemType, Report } from "types";
import { Currency } from "../../../Common/Currency";
import { Loader } from "../../../Loader";
import { ProductRenderer } from "../ColumnTypes";
import { CustomReport } from "../CustomReport";
import { ReportColumnDef, ReportColumnFilterValueType } from "../SuggestedReports";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";

export const ReservationsByListingReport = ({ report }: { report: Report }) => {
    const { isLoading } = useAutoFetchCollection<LineItemTemplate>("lineItemTemplates");
    return isLoading ? (
        <Loader />
    ) : (
        <CustomReport dateFields={["createdAt", "arrivalDate"]} report={report} columns={columns} unwind={unwind} />
    );
};

export interface GroupedExperienceData {
    lineItems: LineItem[];
    amount: number;
    quantity: number;
    product: { id: ID };
    name: string;
}

// MIST purchases (multiple products in one transaction) can group merchandise/membership items
// under the same listing as the experience. Those items carry a WARE/MEMBERSHIP line item instead
// of a DEMOGRAPHIC one, so use that to exclude them from this experience-only report.
const nonExperienceLineItemTypes = new Set([LineItemType.WARE, LineItemType.MEMBERSHIP]);

export interface GroupedExperienceRow {
    items: GroupedExperienceData[];
}

export const unwind = (data: GroupedExperienceRow[]) => {
    return data.filter((row) => {
        return !row.items.some((item) =>
            item.lineItems.some((lineItem) => nonExperienceLineItemTypes.has(lineItem.type)),
        );
    });
};

const LineItemTotalRenderer = ({ value, isCurrency }: { value: number | null; isCurrency?: boolean }) => {
    if (!value || almostZero(value)) return null;

    return isCurrency ? <Currency shouldRemoveTrailingZeroes={false}>{value}</Currency> : value;
};

const columns: ReportColumnDef[] = [
    {
        field: "createdAt",
        headerName: "Booking Date",
        type: "datetime",
        filterValueType: ReportColumnFilterValueType.DATE,
        hide: true,
    },
    {
        field: "arrivalDate",
        headerName: "Arrival Date",
        type: "date",
        filterValueType: ReportColumnFilterValueType.DATE,
        hide: true,
    },
    {
        field: "items.realizedAt",
        headerName: "Realized Date",
        type: "datetime",
        filterValueType: ReportColumnFilterValueType.DATE,
        hide: true,
    },
    {
        field: "items.product",
        headerName: "Product",
        type: "product",
        maxWidth: 250,
        valueGetter: ({ data }: { data: { items: GroupedExperienceData[] } }) => data.items[0]?.product?.id,
        cellRenderer: ProductRenderer,
    },
    {
        field: "items.amount",
        headerName: "Total Value",
        valueGetter: ({ data }: { data: { items: GroupedExperienceData[] } }) => data.items[0]?.amount,
        type: "currency",
        filter: false,
    },
    {
        field: "items.*.quantity",
        headerName: "Total Booked",
        type: "number",
        minWidth: 120,
        filter: false,
        valueGetter: ({ data }: { data: { items: GroupedExperienceData[] } }) => data.items[0]?.quantity,
        expand: {
            key: "items.*.lineItems.*",
            type: "number",
            minWidth: 130,
            filter: false,
            headerGetter: (lineItems: LineItem[]) =>
                getMatchingHeaders(lineItems, [LineItemType.DEMOGRAPHIC, LineItemType.DEMOGRAPHIC_SECONDARY]),
            valueGetter: (params) =>
                getLineItemTotal(params, "quantity", [LineItemType.DEMOGRAPHIC, LineItemType.DEMOGRAPHIC_SECONDARY]),
            cellRenderer: LineItemTotalRenderer,
            cellRendererParams: { isCurrency: false },
        },
    },
    {
        field: "items.*.equipment",
        headerName: "Equipment Booked",
        type: "number",
        minWidth: 120,
        filter: false,
        // Equipment counts units, not people, so it stays out of the demographic breakdown above.
        valueGetter: ({ data }: { data: { items: GroupedExperienceData[] } }) =>
            sumBy(
                (data.items[0]?.lineItems ?? []).filter(
                    (lineItem: LineItem) => lineItem.type === LineItemType.EQUIPMENT,
                ),
                "quantity",
            ),
        expand: {
            key: "items.*.lineItems.*",
            type: "number",
            minWidth: 130,
            filter: false,
            headerGetter: (lineItems: LineItem[]) => getMatchingHeaders(lineItems, [LineItemType.EQUIPMENT]),
            valueGetter: (params) => getLineItemTotal(params, "quantity", [LineItemType.EQUIPMENT]),
            cellRenderer: LineItemTotalRenderer,
            cellRendererParams: { isCurrency: false },
        },
    },
    {
        field: "items.*.addon",
        headerName: "Add-ons Total",
        type: "currency",
        minWidth: 120,
        filter: false,
        valueGetter: ({ data }: { data: { items: GroupedExperienceData[] } }) =>
            getSubtotalAmount(data, [LineItemCode.ADDON, LineItemCode.PICKUP]),
        expand: {
            key: "items.*.lineItems.*",
            type: "number",
            minWidth: 130,
            filter: false,
            headerGetter: (lineItems: LineItem[]) =>
                getMatchingHeaders(lineItems, [LineItemType.ADDON, LineItemType.PICKUP]),
            valueGetter: (params) => getLineItemTotal(params, "quantity", [LineItemType.ADDON, LineItemType.PICKUP]),
            cellRenderer: LineItemTotalRenderer,
            cellRendererParams: {
                isCurrency: false,
            },
        },
    },
    {
        field: "baseAmount",
        headerName: "Base Amount",
        type: "currency",
        filterParams: {
            maxNumConditions: 1,
        },
        valueGetter: ({ data }: { data: { items: GroupedExperienceData[] } }) => {
            const baseAmount = data.items[0]?.lineItems?.find(
                (lineItem: LineItem) => lineItem.code === LineItemCode.DEMOGRAPHIC,
            )?.amount;
            if (baseAmount !== undefined) {
                return baseAmount;
            }

            // Equipment items carry no demographic line item, so total what they actually charge for.
            return sumBy(data.items[0]?.lineItems ?? [], (lineItem: LineItem) =>
                [LineItemType.EQUIPMENT, LineItemType.DEMOGRAPHIC_SECONDARY].includes(lineItem.type) &&
                lineItem.includeInAmount
                    ? lineItem.amount ?? 0
                    : 0,
            );
        },
    },
    {
        field: "items.*.tax",
        headerName: "Taxes & Fees Total",
        type: "currency",
        filter: false,
        valueGetter: ({ data }: { data: { items: GroupedExperienceData[] } }) =>
            getSubtotalAmount(data, [LineItemCode.TAX]),
        expand: {
            key: "items.*.lineItems.*",
            type: "currency",
            filter: false,
            headerGetter: (lineItems: LineItem[]) => getMatchingHeaders(lineItems, [LineItemType.TAX]),
            valueGetter: (params) => getLineItemTotal(params, "amount", [LineItemType.TAX]),
            cellRenderer: LineItemTotalRenderer,
            cellRendererParams: { isCurrency: true },
        },
    },
    {
        field: "affiliateCommission",
        headerName: "Partner Commission",
        type: "currency",
        minWidth: 180,
        maxWidth: 200,
        filter: false,
        valueGetter: ({ data }: { data: { items: GroupedExperienceData[] } }) => {
            const commissionLineItem = data.items[0]?.lineItems?.find(
                (lineItem) => lineItem.type === LineItemType.AFFILIATE_COMMISSION,
            );
            return commissionLineItem ? -1 * commissionLineItem.amount : "";
        },
    },
];

const getMatchingHeaders = (lineItems: LineItem[], type: LineItemType[]): string[] => {
    return lineItems.filter((lineItem) => type.includes(lineItem.type)).map((lineItem) => lineItem.label);
};

const getSubtotalAmount = (data: { items: GroupedExperienceData[] }, code: LineItemCode[]) => {
    return sumBy(data.items[0]?.lineItems ?? [], (lineItem: LineItem) => {
        const isCorrectItem = lineItem.type === LineItemType.SUBTOTAL && code.includes(lineItem.code);
        return isCorrectItem ? lineItem.amount : 0;
    });
};

const getLineItemTotal = (
    params: { data: { items: GroupedExperienceData[] }; colDef: { headerName?: string } },
    field: "amount" | "quantity",
    lineItemType: LineItemType[],
) => {
    const data = params.data;
    const lineItems = data.items[0]?.lineItems ?? [];
    const total = sumBy(lineItems, (lineItem: LineItem) => {
        const isCorrectItem = lineItemType.includes(lineItem.type);
        const value = lineItem[field];
        const name = lineItem.name ?? lineItem.label;
        return isCorrectItem && name === params.colDef.headerName ? Number(value) : 0;
    });

    return total;
};
